import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { usePrices } from "@/contexts/prices-provider";
import { useTeams } from "@/contexts/teams-provider";
import { useTranslation } from "@/hooks/useTranslation";
import { SubscriptionType } from "@shaarp/common";
import { useEffect, useState } from "react";
import { LicenseCountSelector } from "./license-count-selector";
import { SubscriptionTypeSelector } from "./subscription-type-selector";

export const PlanSelection = ({
  hideTrialInfo = false,
  defaultSubscriptionType,
  defaultProLicenseCountMonthly,
  defaultProLicenseCountAnnual,
  onSubscriptionTypeChange = () => {},
  onPendingProLicenseCountChange = () => {},
  readOnly = false,
}: {
  hideTrialInfo?: boolean;
  defaultSubscriptionType?: SubscriptionType;
  defaultProLicenseCountMonthly?: number | null;
  defaultProLicenseCountAnnual?: number | null;
  onSubscriptionTypeChange?: (subscriptionType: SubscriptionType) => void;
  onPendingProLicenseCountChange?: (pendingProLicenseCount: number) => void;
  readOnly?: boolean;
}) => {
  const { t } = useTranslation();
  const { teamSelected } = useTeams();
  const { prices, apiFetchPrices } = usePrices();

  useEffect(() => {
    apiFetchPrices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The admin predefines a starting count for exactly one frequency; the other
  // stays null. Presence of a value — not its magnitude — is what marks a
  // frequency as admin-predefined (a floor of 1 is still a deliberate choice).
  const monthlyPredefined = defaultProLicenseCountMonthly != null;
  const annualPredefined = defaultProLicenseCountAnnual != null;

  const monthlyFloor = defaultProLicenseCountMonthly || 1;
  const annualFloor = defaultProLicenseCountAnnual || 1;

  const floorForFrequency = (type: SubscriptionType): number =>
    type === "annual" ? annualFloor : monthlyFloor;

  // Surface both frequencies' admin-predefined starting counts at once so the
  // user doesn't have to toggle frequency to discover them.
  const hasPredefinedFloor = monthlyPredefined || annualPredefined;

  // When the admin predefined a starting count for one frequency, that
  // frequency is locked: it's pre-selected and the other one is disabled, since
  // a subscription can't mix billing intervals.
  const lockedFrequency: SubscriptionType | null = annualPredefined
    ? "annual"
    : monthlyPredefined
      ? "monthly"
      : null;

  const initialFrequency: SubscriptionType =
    lockedFrequency ?? defaultSubscriptionType ?? "monthly";

  const [subscriptionType, setSubscriptionType] =
    useState<SubscriptionType>(initialFrequency);

  const currentFloor = floorForFrequency(subscriptionType);

  const [pendingProLicenseCount, setPendingProLicenseCount] = useState<number>(
    floorForFrequency(initialFrequency)
  );

  // Sync the parent (which drives the checkout) with the locked frequency and
  // its floor on mount, in case its default differs from the locked value.
  useEffect(() => {
    if (lockedFrequency) {
      onSubscriptionTypeChange(lockedFrequency);
      onPendingProLicenseCountChange(floorForFrequency(lockedFrequency));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePendingLicenseChange = (newCount: number) => {
    if (newCount < currentFloor) {
      return;
    }
    setPendingProLicenseCount(newCount);
    onPendingProLicenseCountChange(newCount);
  };

  const updateSubscriptionType = (newType: SubscriptionType) => {
    // A locked frequency can't be changed.
    if (lockedFrequency && newType !== lockedFrequency) {
      return;
    }
    setSubscriptionType(newType);
    onSubscriptionTypeChange?.(newType);

    // Enforce the new frequency's floor: bump the pending count up if it now
    // sits below the minimum the admin predefined for this frequency.
    const newFloor = floorForFrequency(newType);
    if (pendingProLicenseCount < newFloor) {
      setPendingProLicenseCount(newFloor);
      onPendingProLicenseCountChange(newFloor);
    }
  };

  const getSelectedPriceUnit = (): number => {
    const interval = subscriptionType === "annual" ? "year" : "month";
    const proPrice = prices.find(
      (price) =>
        price.licenceType === "professional" && price.interval === interval
    );
    const basePrice: number = proPrice?.amount ?? 0;
    const discount: number | undefined =
      subscriptionType === "annual"
        ? teamSelected?.annualDiscount
        : teamSelected?.monthlyDiscount;

    const price: number = discount
      ? basePrice - (basePrice * discount) / 100
      : basePrice;

    return Number.isInteger(price) ? price : parseFloat(price.toFixed(2));
  };

  const getPriceTotal = (): number => {
    const priceUnit: number = getSelectedPriceUnit();
    return parseFloat((priceUnit * pendingProLicenseCount).toFixed(2));
  };

  const features = [
    t("setup.paymentMethod.plan.features.0"),
    t("setup.paymentMethod.plan.features.1"),
    t("setup.paymentMethod.plan.features.2"),
    t("setup.paymentMethod.plan.features.3"),
    t("setup.paymentMethod.plan.features.4"),
    t("setup.paymentMethod.plan.features.5"),
  ];

  return (
    <div className="space-y-4">
      {!hideTrialInfo && !!teamSelected?.trialCountDays ? (
        <Card className="border">
          <CardContent className="flex justify-between p-4">
            <span>
              <strong>
                {t("setup.paymentMethod.trial.days", {
                  count: teamSelected?.trialCountDays,
                })}
              </strong>
            </span>
            <Badge style={{ fontSize: 10 }}>
              {t("setup.paymentMethod.trial.free")}
            </Badge>
          </CardContent>
        </Card>
      ) : !hideTrialInfo && !!teamSelected?.trialEndsAt ? (
        <Card className="border">
          <CardContent className="flex justify-between p-6">
            <div>
              {t("setup.paymentMethod.trial.until", {
                date: new Date(teamSelected?.trialEndsAt).toLocaleDateString(),
              })}
            </div>
            <Badge style={{ fontSize: 10 }}>
              {t("setup.paymentMethod.trial.free")}
            </Badge>
          </CardContent>
        </Card>
      ) : null}

      <Card className="border">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">
            {t("setup.paymentMethod.plan.title")}
          </CardTitle>
          <CardDescription className="flex flex-col text-xl font-bold">
            <hr className="my-4" />
            <span className="text-primary">
              {subscriptionType === "monthly" &&
                t("setup.paymentMethod.plan.price.monthly", {
                  price: getSelectedPriceUnit(),
                })}
              {subscriptionType === "annual" &&
                t("setup.paymentMethod.plan.price.annual", {
                  price: getSelectedPriceUnit(),
                })}
            </span>
            {subscriptionType === "monthly" && (
              <span className="text-sm font-normal text-muted-foreground">
                {t("setup.paymentMethod.plan.price.noCommitment")}
              </span>
            )}
          </CardDescription>
        </CardHeader>
      </Card>

      {!readOnly && (
        <SubscriptionTypeSelector
          subscriptionType={subscriptionType}
          onSubscriptionTypeChange={updateSubscriptionType}
          monthlyDiscount={teamSelected?.monthlyDiscount}
          annualDiscount={teamSelected?.annualDiscount}
          disabledType={
            lockedFrequency
              ? lockedFrequency === "monthly"
                ? "annual"
                : "monthly"
              : undefined
          }
        />
      )}

      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between gap-2 mb-4">
            <span className="text-sm text-muted-foreground">
              {t("setup.paymentMethod.plan.price.nbProLicense")} :
            </span>

            {!readOnly ? (
              <div className="flex items-center gap-2">
                <LicenseCountSelector
                  value={pendingProLicenseCount}
                  onValueChange={handlePendingLicenseChange}
                  min={currentFloor}
                />
              </div>
            ) : (
              <span className="font-bold">{currentFloor}</span>
            )}
          </div>

          <div className="flex items-center justify-between gap-2">
            <span className="text-sm text-muted-foreground">
              {t("setup.paymentMethod.plan.price.total")}
            </span>
            <span className="font-bold">
              {subscriptionType === "annual"
                ? t("setup.paymentMethod.plan.price.annualTotal", {
                    price: getPriceTotal(),
                  })
                : t("setup.paymentMethod.plan.price.monthlyTotal", {
                    price: getPriceTotal(),
                  })}
            </span>
          </div>

          {hasPredefinedFloor && (
            <div className="mt-4 border-t pt-3">
              <p className="mb-1 text-xs font-medium text-muted-foreground">
                {t("setup.paymentMethod.plan.price.initialLicensesLabel")}
              </p>
              <div className="flex items-center gap-4 text-sm">
                {monthlyPredefined && (
                  <span className="text-muted-foreground">
                    {t(
                      "setup.paymentMethod.plan.price.initialLicensesMonthly",
                      { count: monthlyFloor }
                    )}
                  </span>
                )}
                {annualPredefined && (
                  <span className="text-muted-foreground">
                    {t("setup.paymentMethod.plan.price.initialLicensesAnnual", {
                      count: annualFloor,
                    })}
                  </span>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border">
        <CardContent className="p-6 space-y-2">
          {features.map((feature, index) => (
            <div key={index} className="flex items-center gap-2 text-left">
              <span className="text-primary">✓</span>
              <span>{feature}</span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};
